import {
    Cell,
    ColumnDef,
    ColumnFiltersState,
    ColumnPinningPosition,
    ColumnPinningState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    OnChangeFn,
    PaginationState,
    PartialKeys,
    RowData,
    RowSelectionState,
    type Table,
    TableOptionsResolved,
    useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { css, Stack, styled, useTheme } from '@mui/material'
import { isEmpty } from 'lodash'
import { isKeyOf, tokens } from 'ComponentLibrary'
import { memo, useCallback, useMemo, useRef } from 'react'
import invariant from 'tiny-invariant'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react'

declare module '@tanstack/react-table' {
    // This is needed to ensure that filterFns stays optional after adding our custom dictionaryFilter as an option
    // ref: https://github.com/TanStack/table/issues/5005#issuecomment-2118846886
    interface TableOptions<TData extends RowData> extends PartialKeys<
        TableOptionsResolved<TData>,
        'state' | 'onStateChange' | 'renderFallbackValue'
    > {
        filterFns?: FilterFns
    }

    interface FilterFns {
        dictionaryFilter: FilterFn<unknown>
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TanStack declaration merging requires these exact type parameter names
    interface ColumnMeta<TData extends RowData, TValue> {
        /** Leaves this column unsized so it receives the remaining table width when controlledSizing is enabled. */
        fillAvailableWidth?: boolean
    }
}

type CommonTableProps<TData> = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see existing issue: https://github.com/TanStack/table/issues/4382
    columns: ColumnDef<TData, any>[]
    data: TData[]
    columnPinning?: ColumnPinningState
    backgroundColor?: string
    pagination?: Partial<PaginationState>
    maxWidth?: string
    maxHeight?: string
    showNumberOfEntries?: boolean
    disabled?: boolean
    columnFilters?: ColumnFiltersState
    setColumnFilters?: OnChangeFn<ColumnFiltersState>
    rowSelection?: RowSelectionState
    setRowSelection?: OnChangeFn<RowSelectionState>
    getRowId?: (originalRow: TData) => string
    isFiltered?: boolean
    width?: string
    disableVirtualization?: boolean
    copy?: Partial<SimpleTableCopy>
}

type TablePropsWithControlledWidth<TData> = {
    controlledSizing?: false
    enableColumnResizing?: false
} & CommonTableProps<TData>

type TablePropsWithControlledColumns<TData> = {
    controlledSizing: true
    enableColumnResizing?: boolean
} & CommonTableProps<TData>

type TableProps<TData> = TablePropsWithControlledWidth<TData> | TablePropsWithControlledColumns<TData>

const estimateRowHeight = 33

export type SimpleTableCopy = {
    entriesLabel: (count: number) => string
    selectedEntriesLabel: (selectedCount: number, totalCount: number) => string
    pageLabel: (page: number, pageCount: number) => string
    emptyFiltered: string
    empty: string
}

const DEFAULT_SIMPLE_TABLE_COPY: SimpleTableCopy = {
    entriesLabel: (count) => `${count} ${count === 1 ? 'Eintrag' : 'Einträge'}`,
    selectedEntriesLabel: (selectedCount, totalCount) =>
        `${selectedCount} von ${totalCount} ${totalCount === 1 ? 'Eintrag' : 'Einträgen'} ausgewählt`,
    pageLabel: (page, pageCount) => `${page} von ${pageCount} Seite`,
    emptyFiltered: 'Keine Einträge zu Ihrer Suche',
    empty: 'Keine Einträge vorhanden',
}

export function SimpleTable<TData>({
    data,
    columns,
    columnPinning,
    backgroundColor,
    pagination,
    width,
    maxWidth,
    maxHeight,
    showNumberOfEntries,
    controlledSizing,
    enableColumnResizing = false,
    disabled,
    columnFilters,
    setColumnFilters,
    rowSelection,
    setRowSelection,
    getRowId,
    isFiltered,
    disableVirtualization,
    copy,
}: TableProps<TData>) {
    const theme = useTheme()
    const tableCopy = { ...DEFAULT_SIMPLE_TABLE_COPY, ...copy }
    const table = useReactTable({
        data,
        columns,
        getRowId,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
        getFilteredRowModel: getFilteredRowModel(),
        initialState: {
            pagination,
        },
        state: {
            columnFilters,
            rowSelection: rowSelection ?? {},
            ...(columnPinning && { columnPinning }),
        },
        onRowSelectionChange: setRowSelection,
        onColumnFiltersChange: setColumnFilters,
        filterFns: {
            dictionaryFilter: (row, columnId, filterValue: { [key: string]: boolean }) => {
                if (!Object.values(filterValue).includes(true)) {
                    return true
                }
                invariant(
                    typeof row.original === 'object' && !!row.original && isKeyOf(columnId, row.original),
                    `Unable to access property ${columnId} in object ${JSON.stringify(row.original)}`
                )
                return !!filterValue[row.original[columnId]]
            },
        },
        enableColumnResizing,
        columnResizeMode: 'onChange',
        defaultColumn: {
            minSize: 40,
            maxSize: 500,
        },
    })

    const tableState = table.getState()

    const columnSizes = useMemo(() => {
        const headers = table.getFlatHeaders()
        const colSizes: { [key: string]: number } = {}
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i]
            colSizes[`header${header.id}`] = header.getSize()
            colSizes[`col${header.column.id}`] = header.column.getSize()
        }
        return colSizes
        // tableState.columnSizingInfo is included to recompute on resize events even though we don't read it directly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState.columnSizingInfo, table])

    const hasRows = !isEmpty(data)
    // code taken from https://github.com/TanStack/table/discussions/5023#discussioncomment-8572450
    const hasFooters =
        table
            .getFooterGroups()
            .map((group) => group.headers.map((header) => header.column.columnDef.footer))
            .flat()
            .filter(Boolean).length > 0

    const allRows = table.getRowModel().rows

    const getItemKey = useCallback((index: number) => {
        return allRows[index]?.id ?? index
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const scrollParentRef = useRef<HTMLDivElement | null>(null)
    const rowVirtualizer = useVirtualizer({
        count: allRows.length,
        getScrollElement: () => scrollParentRef.current,
        estimateSize: () => estimateRowHeight,
        overscan: 8,
        getItemKey,
        enabled: !disableVirtualization,
    })
    const virtualRows: VirtualItem[] = disableVirtualization
        ? allRows.map((_, index) => ({
              key: `${allRows[index]?.id ?? index}`,
              index,
              start: index * estimateRowHeight,
              end: (index + 1) * estimateRowHeight,
              size: estimateRowHeight,
              lane: 0,
          }))
        : rowVirtualizer.getVirtualItems()
    const totalSize = disableVirtualization ? allRows.length * estimateRowHeight : rowVirtualizer.getTotalSize()
    const bufferTop = virtualRows.length > 0 ? virtualRows[0].start : 0
    const bufferBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0

    const numberOfRows = data.length
    const numberOfSelectedRows = Object.keys(tableState.rowSelection ?? {}).length

    return (
        <>
            <div
                ref={scrollParentRef}
                style={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxWidth,
                    maxHeight,
                    pointerEvents: disabled ? 'none' : undefined,
                    opacity: disabled ? '0.5' : undefined,
                }}
            >
                <table
                    width={width}
                    style={{
                        borderCollapse: 'collapse',
                        borderSpacing: 0,
                        tableLayout: controlledSizing ? 'fixed' : undefined,
                    }}
                >
                    {controlledSizing && (
                        <colgroup>
                            {table.getVisibleLeafColumns().map((column) => (
                                <col
                                    key={column.id}
                                    style={{ width: column.columnDef.meta?.fillAvailableWidth ? undefined : `${column.getSize()}px` }}
                                />
                            ))}
                        </colgroup>
                    )}
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <StyledHeadingCell
                                        key={header.id}
                                        $pinned={header.column.getIsPinned()}
                                        $leftOffset={header.column.getStart('left')}
                                        $rightOffset={header.column.getAfter('right')}
                                        colSpan={header.colSpan}
                                        $width={
                                            controlledSizing && !header.column.columnDef.meta?.fillAvailableWidth
                                                ? `${columnSizes[`header${header.id}`]}px`
                                                : undefined
                                        }
                                        $backgroundColor={backgroundColor}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanResize() && (
                                            <Resizer onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()} />
                                        )}
                                    </StyledHeadingCell>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    {tableState.columnSizingInfo.isResizingColumn ? (
                        <MemoizedTableBody
                            table={table}
                            hasRows={hasRows}
                            columnSizes={columnSizes}
                            controlledSizing={controlledSizing}
                            backgroundColor={backgroundColor}
                            virtual={{ bufferTop, bufferBottom, virtualRows }}
                            enableRowSelection={!!setRowSelection}
                            copy={tableCopy}
                        />
                    ) : (
                        <TableBody
                            table={table}
                            hasRows={hasRows}
                            columnSizes={columnSizes}
                            controlledSizing={controlledSizing}
                            isFiltered={isFiltered}
                            backgroundColor={backgroundColor}
                            virtual={{ bufferTop, bufferBottom, virtualRows }}
                            enableRowSelection={!!setRowSelection}
                            copy={tableCopy}
                        />
                    )}

                    {table.getRowModel().rows.length > 0 && hasFooters && (
                        <tfoot>
                            {table.getFooterGroups().map((footerGroup, groupIndex) => {
                                if (groupIndex !== 0) {
                                    return null
                                }
                                return (
                                    <tr key={footerGroup.id}>
                                        {footerGroup.headers.map((header) => (
                                            <StyledFooterHeader
                                                scope="row"
                                                key={header.id}
                                                $pinned={header.column.getIsPinned()}
                                                $leftOffset={header.column.getStart('left')}
                                                $rightOffset={header.column.getAfter('right')}
                                                $backgroundColor={backgroundColor}
                                            >
                                                {flexRender(header.column.columnDef.footer, header.getContext())}
                                            </StyledFooterHeader>
                                        ))}
                                    </tr>
                                )
                            })}
                        </tfoot>
                    )}
                </table>
            </div>
            {(showNumberOfEntries || pagination) && (
                <Stack direction="row" justifyContent="space-between" width={width} marginTop={tokens.SizingSpacing150} alignItems="center">
                    {showNumberOfEntries && (
                        <Typography variant="textS">
                            {numberOfSelectedRows > 0
                                ? tableCopy.selectedEntriesLabel(numberOfSelectedRows, numberOfRows)
                                : tableCopy.entriesLabel(numberOfRows)}
                        </Typography>
                    )}
                    {pagination && (
                        <Stack direction="row" marginLeft="auto" gap={tokens.SizingSpacing075}>
                            <IconButton
                                icon={<ArrowLeftIcon />}
                                onClick={table.previousPage}
                                disabled={!table.getCanPreviousPage()}
                                size={'small'}
                                variant="secondary"
                            />
                            <Stack alignItems="center" gap={theme.spacing(0.5)} direction="row">
                                <Typography variant="textS">
                                    {tableCopy.pageLabel(table.getState().pagination.pageIndex + 1, table.getPageCount() || 1)}
                                </Typography>
                            </Stack>
                            <IconButton
                                icon={<ArrowRightIcon />}
                                onClick={table.nextPage}
                                disabled={!table.getCanNextPage()}
                                size={'small'}
                                variant="secondary"
                            />
                        </Stack>
                    )}
                </Stack>
            )}
        </>
    )
}

type TableBodyProps<TData> = {
    table: Table<TData>
    hasRows: boolean
    columnSizes: { [key: string]: number }
    controlledSizing?: boolean
    isFiltered?: boolean
    virtual: {
        bufferTop: number
        bufferBottom: number
        virtualRows: VirtualItem[]
    }
    backgroundColor?: string
    enableRowSelection?: boolean
    copy: SimpleTableCopy
}

function BodyCell<TData>({
    cell,
    columnSizes,
    controlledSizing,
    backgroundColor,
}: {
    cell: Cell<TData, unknown>
    columnSizes: { [key: string]: number }
    controlledSizing?: boolean
    backgroundColor?: string
}) {
    return (
        <StyledBodyCell
            $pinned={cell.column.getIsPinned()}
            $leftOffset={cell.column.getStart('left')}
            $rightOffset={cell.column.getAfter('right')}
            $width={
                controlledSizing && !cell.column.columnDef.meta?.fillAvailableWidth ? `${columnSizes[`col${cell.column.id}`]}px` : undefined
            }
            $backgroundColor={backgroundColor}
        >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </StyledBodyCell>
    )
}

function TableBody<TData>({
    table,
    hasRows,
    columnSizes,
    controlledSizing,
    isFiltered,
    virtual,
    backgroundColor,
    enableRowSelection,
    copy,
}: TableBodyProps<TData>) {
    const emptyText = isFiltered ? copy.emptyFiltered : copy.empty
    const rows = table.getRowModel().rows

    if (!hasRows) {
        return (
            <tbody>
                <tr>
                    <StyledBodyCell colSpan={table.getAllLeafColumns().length} $backgroundColor={backgroundColor}>
                        <EmptyStateCell>
                            <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                {emptyText}
                            </Typography>
                        </EmptyStateCell>
                    </StyledBodyCell>
                </tr>
            </tbody>
        )
    }

    return (
        <tbody>
            {virtual.bufferTop > 0 && (
                <tr>
                    <td colSpan={table.getAllLeafColumns().length} style={{ height: `${virtual.bufferTop}px` }} />
                </tr>
            )}

            {virtual.virtualRows.map((vRow) => {
                const row = rows[vRow.index]
                const isSelected = enableRowSelection ? row.getIsSelected() : undefined

                return (
                    <StyledBodyRow
                        key={row.id}
                        $selectable={enableRowSelection}
                        $selected={isSelected}
                        onClick={enableRowSelection ? () => row.toggleSelected() : undefined}
                    >
                        {row.getVisibleCells().map((cell) => (
                            <BodyCell
                                key={cell.id}
                                cell={cell}
                                columnSizes={columnSizes}
                                controlledSizing={controlledSizing}
                                backgroundColor={backgroundColor}
                            />
                        ))}
                    </StyledBodyRow>
                )
            })}

            {virtual.bufferBottom > 0 && (
                <tr>
                    <td colSpan={table.getAllLeafColumns().length} style={{ height: `${virtual.bufferBottom}px` }} />
                </tr>
            )}
        </tbody>
    )
}

const MemoizedTableBody = memo(TableBody, (prev, next) => prev.table.options.data === next.table.options.data) as typeof TableBody

const EmptyStateCell = styled('div')(() => ({
    width: '100%',
    borderBottom: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    height: '32px',
    boxSizing: 'border-box',
}))

const StyledHeadingCell = styled('th', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{
    $pinned?: ColumnPinningPosition
    $leftOffset?: number
    $rightOffset?: number
    $width?: string
    $backgroundColor?: string
}>(({ $pinned, $leftOffset, $rightOffset, $width, $backgroundColor }) => ({
    ...($pinned && {
        position: 'sticky',
        zIndex: 1,
        ...($pinned === 'right' && { right: $rightOffset }),
        ...($pinned === 'left' && { left: $leftOffset }),
    }),
    backgroundColor: $backgroundColor ?? undefined,
    width: $width ?? undefined,
    minWidth: $width ?? undefined,
    padding: 0,
}))

type CellProps = {
    $pinned?: ColumnPinningPosition
    $width?: string
    $backgroundColor?: string
    $leftOffset?: number
    $rightOffset?: number
}

const StyledBodyRow = styled('tr', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{
    $selectable?: boolean
    $selected?: boolean
}>(({ $selectable, $selected }) => ({
    cursor: $selectable ? 'pointer' : 'default',
    '& > td': {
        backgroundColor: $selected ? tokens.ColorSurfaceSelectedDefault : undefined,
    },
}))

const sharedBodyCellStyles = ({ $pinned, $width, $backgroundColor, $leftOffset, $rightOffset }: CellProps) =>
    css({
        whiteSpace: 'nowrap',
        ...($pinned && {
            position: 'sticky',
            ...($pinned === 'right' && { right: $rightOffset }),
            ...($pinned === 'left' && { left: $leftOffset }),
        }),
        backgroundColor: $backgroundColor ?? undefined,
        width: $width ?? undefined,
        minWidth: $width ?? undefined,
        overflow: 'hidden',
        padding: 0,
    })

const StyledBodyCell = styled('td', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<CellProps>(sharedBodyCellStyles)

const StyledFooterHeader = styled('th', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<CellProps>(sharedBodyCellStyles)

const Resizer = styled('div')(() => ({
    position: 'absolute',
    right: 0,
    top: 0,
    marginRight: '-4px',
    zIndex: 1,
    width: '7px',
    height: '27.5px',
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
}))
